import { useMemo, useState } from 'react';
import i18nInstance from '../../../i18n';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import { isFormType, type FormType } from '../../../api/forms';
import { useDeleteDraft, useDraft, usePublishDraft, usePublished, useSaveDraft, useTemplateVersion, validateDraft } from '../../../api/templates';
import { useEnumLabel } from '../../../api/enums';
import { isApiError } from '../../../api/errors';
import { Breadcrumbs, Button, ButtonGroup, ButtonLink, ConfirmDialog, ErrorSummary, type ErrorSummaryItem } from '../../../components/idsk';
import { useNavigateWithToast } from '../../../components/common/navigateWithToast';
import { VersionConflictSummary } from '../../../components/common/VersionConflict';
import { PageHeading } from '../../../layout/PageHeading';
import { usePageTitle } from '../../../layout/usePageTitle';
import { flatten, type FieldDef, type FieldType, type FormDefinition } from '../../../form-engine';
import { text } from '../../../form-engine/types';
import { NotFoundPage } from '../../common/NotFoundPage';
import { Loading, LoadingPage } from '../../common/LoadingPage';
import { QueryError } from '../../common/QueryError';
import { RequireAdmin } from '../../common/RequireAdmin';
import { FieldList } from '../builder/FieldList';
import { FieldEditor } from '../builder/FieldEditor';
import { FieldActions, FieldLinks } from '../builder/FieldActions';
import { FormCanvas, canvasEditId } from '../builder/FormCanvas';
import { ControlPalette, usePaletteTargets } from '../builder/ControlPalette';
import { FieldMovePage } from '../builder/FieldMovePage';
import { PalettePage } from '../builder/PalettePage';
import { isGroup, paletteQuery, positionParam, type PaletteItem } from '../builder/palette';
import { FormPreview, previewContext } from '../builder/FormPreview';
import { locate, tableLock } from '../builder/definitionOps';
import { StatusTag } from './TemplateDetailPage';
import { ExportTemplateButton } from './TemplateExport';

function useFormTypeParam(): FormType | undefined {
  const { type } = useParams();
  return isFormType(type) ? type : undefined;
}

/** Keys, option values and table columns / rows of the published version: immutable in the draft. */
function locks(published: FormDefinition | null | undefined) {
  const keys = new Set<string>();
  const options = new Set<string>();
  for (const f of flatten(published?.fields ?? [])) {
    keys.add(f.key);
    (f.options ?? []).forEach((o) => options.add(o.value));
    (f.table?.columns ?? []).forEach((c) => options.add(tableLock('column', c.key)));
    (f.table?.rows ?? []).forEach((r) => options.add(tableLock('row', r.key)));
  }
  return { keys, options };
}

function DraftShell({ children }: { children: (ctx: { type: FormType; name: string }) => React.ReactNode }) {
  const type = useFormTypeParam();
  const label = useEnumLabel();
  if (!type) return <NotFoundPage />;
  return <RequireAdmin>{children({ type, name: label('formTypes', type) })}</RequireAdmin>;
}

export function DraftEditorPage() {
  return <DraftShell>{({ type, name }) => <DraftEditor type={type} name={name} />}</DraftShell>;
}

function DraftEditor({ type, name }: { type: FormType; name: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage === 'en' ? 'en' : 'sk';
  const { key, fieldType } = useParams();
  const [params] = useSearchParams();
  const draft = useDraft(type);
  const published = usePublished(type);
  const save = useSaveDraft(type);
  const publish = usePublishDraft(type);
  const remove = useDeleteDraft(type);
  const navigateWithToast = useNavigateWithToast();
  const [errors, setErrors] = useState<ErrorSummaryItem[]>([]);
  const [conflict, setConflict] = useState(false);
  const [confirm, setConfirm] = useState<'publish' | 'delete' | null>(null);
  const [count, setCount] = useState(0);
  // the live preview of the open field
  const [preview, setPreview] = useState<FieldDef | null>(null);
  const context = useMemo(() => previewContext(t), [t]);
  const [target, setTarget] = useState<string | null>(null);
  const targets = usePaletteTargets(draft.data?.definition?.fields ?? []);
  const definition = draft.data?.definition ?? { fields: [] };
  // the field whose properties are open: an existing one (…/pole/:key) or a new control (…/pole/novy/:fieldType)
  const existing: FieldDef | null = key ? (locate(definition.fields, key)?.field ?? null) : null;
  const parentKey = !key ? params.get('skupina') : null;
  const parent = parentKey ? locate(definition.fields, parentKey)?.field : null;
  const siblings = parent ? (parent.children ?? []) : definition.fields;
  const position = Math.min(positionParam(params) ?? siblings.length, siblings.length);
  const editedType = (existing?.type ?? (!key ? fieldType : undefined)) as FieldType | undefined;
  const selection = editedType ? (existing?.key ?? `new-${editedType}-${parentKey ?? ''}-${position}`) : null;
  // another field opened: the preview of the previous one is dropped at once (state adjusted during render)
  const [previewOf, setPreviewOf] = useState(selection);
  if (previewOf !== selection) {
    setPreviewOf(selection);
    setPreview(null);
  }
  const editTitle = existing ? t('builder.editor.editTitle', { label: text(existing.label, lang) }) : editedType ? t('builder.editor.newTitle', { type: t(`builder.types.${editedType}.name`) }) : null;
  const draftTitle = t('templates.draftTitle', { n: draft.data?.versionNo ?? '' });
  usePageTitle([editTitle, draftTitle, name, t('templates.title')]);
  if (draft.error) return isApiError(draft.error) && draft.error.status === 404 ? <NotFoundPage /> : <QueryError error={draft.error} asPage />;
  if (draft.isPending) return <LoadingPage />;
  // an unknown field, or a new one into something that is not a section / repeating group
  if ((key && !existing) || parent === undefined || (parent && !isGroup(parent))) return <NotFoundPage />;
  const d = draft.data;
  const activeTarget = targets.some((x) => x.key === target) ? target : null;
  const base = `/sablony/${type}/koncept`;
  const { keys, options } = locks(published.data?.definition);

  const fail = (e: unknown) => {
    setCount((c) => c + 1);
    if (isApiError(e) && e.isVersionConflict) return setConflict(true);
    setErrors(isApiError(e) && e.fieldErrors.length ? e.fieldErrors.map((fe) => ({ text: fe.message })) : [{ text: isApiError(e) ? e.message : t('errors.unexpected') }]);
  };

  /** Moves and removals are saved at once; false when the draft was not saved (the error summary says why). */
  const persist = async (next: { definition: FormDefinition }) => {
    setErrors([]);
    try {
      await save.mutateAsync({ definition: next.definition, version: d.version });
      return true;
    } catch (e) {
      fail(e);
      return false;
    }
  };

  const startPublish = async () => {
    setErrors([]);
    try {
      const result = await validateDraft(type);
      if (!result.valid) {
        setCount((c) => c + 1);
        setErrors(result.errors.map((e) => ({ text: e.message })));
        return;
      }
      setConfirm('publish');
    } catch (e) {
      fail(e);
    }
  };

  return (
    <>
      <Breadcrumbs items={[{ text: t('nav.home'), to: '/' }, { text: t('templates.title'), to: '/sablony' }, { text: name, to: `/sablony/${type}` }, { text: t('templates.draftTitle', { n: d.versionNo }) }]} />
      {conflict && <VersionConflictSummary onReload={() => void draft.refetch().then(() => setConflict(false))} focusKey={count} />}
      {errors.length > 0 && <ErrorSummary title={t('templates.publishErrors')} errors={errors} focusKey={count} />}
      <PageHeading caption={name}>{t('templates.draftTitle', { n: d.versionNo })}</PageHeading>
      <p className="govuk-body">
        <StatusTag status={d.status} /> {t('templates.draftIntro')}
      </p>
      <div className="govuk-grid-row app-builder-layout">
        <div className="govuk-grid-column-two-thirds-from-desktop">
          <FormCanvas
            mode="template"
            definition={d.definition!}
            customFields={[]}
            context={context}
            selectPath={(k) => `${base}/pole/${k}`}
            newPath={(p) => `${base}/pole/novy${p ? `?skupina=${p}` : ''}`}
            selected={existing?.key ?? null}
            preview={preview && previewOf === selection ? { field: preview, position: existing ? undefined : { parentKey, index: position } } : null}
            dropPath={(item, p, index) => newFieldPath(type, item, p, index)}
            onChange={persist}
          />
        </div>
        <div className="govuk-grid-column-one-third-from-desktop">
          {editedType ? (
            published.isPending ? (
              <Loading />
            ) : (
              <FieldEditor
                key={selection!}
                mode="template"
                type={editedType}
                definition={d.definition!}
                customFields={[]}
                existing={existing}
                parentKey={parentKey}
                position={existing ? null : position}
                lockedKeys={keys}
                lockedOptions={options}
                context={context}
                backTo={base}
                backFocus={existing ? canvasEditId(existing.key) : 'add-field'}
                initialConstraints={params.get('zobrazenie') === 'select' ? { display: 'select' } : undefined}
                onPreview={setPreview}
                links={
                  existing && (
                    <FieldLinks
                      mode="template"
                      definition={d.definition!}
                      customFields={[]}
                      fieldKey={existing.key}
                      newPath={(p) => `${base}/pole/novy?skupina=${p}`}
                      movePath={(k) => `${base}/pole/${k}/presunut`}
                    />
                  )
                }
                actions={
                  existing && (
                    <FieldActions
                      mode="template"
                      definition={d.definition!}
                      customFields={[]}
                      fieldKey={existing.key}
                      onChange={persist}
                      onRemoved={(message) => navigateWithToast(base, message, { focus: 'add-field' })}
                    />
                  )
                }
                onSave={async ({ definition: next, field }) => {
                  await save.mutateAsync({ definition: next, version: d.version });
                  navigateWithToast(base, t('builder.editor.saved', { label: text(field.label, lang) }), { focus: canvasEditId(field.key) });
                }}
              />
            )
          ) : (
            <ControlPalette targets={targets} target={activeTarget} onTargetChange={setTarget} itemPath={(item, p) => newFieldPath(type, item, p)} draggable />
          )}
        </div>
      </div>
      <ButtonGroup>
        <ButtonLink to={`/sablony/${type}/koncept/nahlad`} variant="secondary">
          {t('templates.preview')}
        </ButtonLink>
        <Button onClick={() => void startPublish()}>{t('templates.publish')}</Button>
        <ExportTemplateButton type={type} versionNo={d.versionNo} name={name} />
        <Button variant="warning" onClick={() => setConfirm('delete')}>
          {t('templates.deleteDraft')}
        </Button>
      </ButtonGroup>
      <ConfirmDialog
        open={confirm === 'publish'}
        title={t('templates.confirmPublish.title', { n: d.versionNo })}
        confirmText={t('templates.confirmPublish.confirm')}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          void publish
            .mutateAsync(d.version)
            .then(() => navigateWithToast(`/sablony/${type}`, t('templates.publishedFlash', { n: d.versionNo })))
            .catch(fail);
        }}
      >
        <p className="govuk-body">{t('templates.confirmPublish.body')}</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm === 'delete'}
        title={t('templates.confirmDelete.title')}
        confirmText={t('templates.confirmDelete.confirm')}
        danger
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          void remove
            .mutateAsync(d.version)
            .then(() => navigateWithToast(`/sablony/${type}`, t('templates.draftDeleted')))
            .catch(fail);
        }}
      >
        <p className="govuk-body">{t('templates.confirmDelete.body')}</p>
      </ConfirmDialog>
    </>
  );
}

function newFieldPath(type: FormType, item: PaletteItem, parent: string | null, index?: number): string {
  return `/sablony/${type}/koncept/pole/novy/${item.type}${paletteQuery(item, parent, index)}`;
}

export function DraftFieldTypePage() {
  return <DraftShell>{({ type, name }) => <DraftPalette type={type} name={name} />}</DraftShell>;
}

function DraftPalette({ type, name }: { type: FormType; name: string }) {
  const draft = useDraft(type);
  const targets = usePaletteTargets(draft.data?.definition?.fields ?? []);
  if (draft.error) return <QueryError error={draft.error} asPage />;
  if (draft.isPending) return <LoadingPage />;
  return <PalettePage backTo={`/sablony/${type}/koncept`} titleParts={[name, t_('templates.title')]} targets={targets} itemPath={(item, parent) => newFieldPath(type, item, parent)} />;
}

export function DraftFieldMovePage() {
  return <DraftShell>{({ type, name }) => <DraftFieldMove type={type} name={name} />}</DraftShell>;
}

function DraftFieldMove({ type, name }: { type: FormType; name: string }) {
  const { t } = useTranslation();
  const { key } = useParams();
  const draft = useDraft(type);
  const save = useSaveDraft(type);
  const navigateWithToast = useNavigateWithToast();
  if (draft.error) return <QueryError error={draft.error} asPage />;
  if (draft.isPending) return <LoadingPage />;
  const definition = draft.data.definition!;
  if (!key || !locate(definition.fields, key)) return <NotFoundPage />;
  return (
    <FieldMovePage
      mode="template"
      definition={definition}
      customFields={[]}
      fieldKey={key}
      backTo={`/sablony/${type}/koncept`}
      titleParts={[name, t('templates.title')]}
      onSave={async ({ definition: next, message }) => {
        await save.mutateAsync({ definition: next, version: draft.data.version });
        navigateWithToast(`/sablony/${type}/koncept`, message);
      }}
    />
  );
}

// translation helper for title parts (resolved at render)
const t_ = (key: string) => i18nInstance.t(key);

export function DraftPreviewPage() {
  return <DraftShell>{({ type, name }) => <DraftPreview type={type} name={name} />}</DraftShell>;
}

function DraftPreview({ type, name }: { type: FormType; name: string }) {
  const { t } = useTranslation();
  const draft = useDraft(type);
  usePageTitle([t('templates.preview'), t('templates.draftTitle', { n: draft.data?.versionNo ?? '' }), name]);
  if (draft.error) return <QueryError error={draft.error} asPage />;
  if (draft.isPending) return <LoadingPage />;
  return (
    <>
      <Breadcrumbs
        items={[
          { text: t('templates.title'), to: '/sablony' },
          { text: name, to: `/sablony/${type}` },
          { text: t('templates.draftTitle', { n: draft.data.versionNo }), to: `/sablony/${type}/koncept` },
          { text: t('templates.preview') },
        ]}
      />
      <PageHeading caption={t('templates.draftTitle', { n: draft.data.versionNo })}>{t('templates.previewTitle', { name })}</PageHeading>
      <p className="govuk-body">{t('templates.previewIntro')}</p>
      <FormPreview formType={type} definition={draft.data.definition!} />
    </>
  );
}

export function VersionPage() {
  return <DraftShell>{({ type, name }) => <Version type={type} name={name} />}</DraftShell>;
}

function Version({ type, name }: { type: FormType; name: string }) {
  const { t } = useTranslation();
  const { no } = useParams();
  const version = useTemplateVersion(type, Number(no));
  const context = useMemo(() => previewContext(t), [t]);
  usePageTitle([t('templates.versionN', { n: no }), name, t('templates.title')]);
  if (version.error) return <QueryError error={version.error} asPage />;
  if (version.isPending) return <LoadingPage />;
  const v = version.data;
  return (
    <>
      <Breadcrumbs items={[{ text: t('templates.title'), to: '/sablony' }, { text: name, to: `/sablony/${type}` }, { text: t('templates.versionN', { n: v.versionNo }) }]} />
      <PageHeading caption={name}>{t('templates.versionN', { n: v.versionNo })}</PageHeading>
      <p className="govuk-body">
        <StatusTag status={v.status} /> {t('templates.readOnly')}
      </p>
      <ButtonGroup>
        <ExportTemplateButton type={type} versionNo={v.versionNo} name={name} />
      </ButtonGroup>
      <FieldList mode="template" definition={v.definition!} customFields={[]} context={context} />
      <h2 className="govuk-heading-m">{t('templates.preview')}</h2>
      <FormPreview formType={type} definition={v.definition!} />
    </>
  );
}
